import React from 'react'
import { formatEuro, AUTOCARRI_INTERMEDIARI_CODICE, calcolaAutocarriIntermediari } from '../../lib/calcoli'
import { isAutocarriIntermediari, PACCHETTI_AUTOCARRO, MASSIMALI_CRISTALLI_AUTOCARRO } from '../../lib/validazioni'

export default function StepComparazione({ data, update, compagnie, standalone, commissioneMax = 50, commissioneAccountPct = 0, autocarriParametri = {}, regione }) {
  if (isAutocarriIntermediari(data.tipo_operazione)) {
    return (
      <StepComparazioneAutocarri
        data={data}
        update={update}
        commissioneMax={commissioneMax}
        commissioneAccountPct={commissioneAccountPct}
        autocarriParametri={autocarriParametri}
      />
    )
  }

  const standaloneTotal = (data.standalone_selezionati || []).reduce((acc, sel) => {
    const prod = standalone.find(p => p.codice === sel.codice)
    if (!prod) return acc
    return acc + parseFloat(prod.prezzo_annuo || 0) * (sel.anni || 1)
  }, 0)

  const haStandalone = standaloneTotal > 0

  // Ricarico scelto dal dealer/subagente per questa pratica (default 0).
  // Si applica al premio finale visibile: polizza + satellitare/GAP + stand-alone.
  const surplus = data.commissione_pct_pratica ?? 0

  function round2(n) { return Math.round(n * 100) / 100 }
  function calcolaRicarico(base) { return round2((parseFloat(base) || 0) * surplus / 100) }
  function totaleConRicarico(base) { return round2((parseFloat(base) || 0) + calcolaRicarico(base)) }

  function setSurplus(val) {
    // Clamp 0..commissioneMax (default 50%)
    const clamped = Math.min(commissioneMax, Math.max(0, parseFloat(val) || 0))
    update({ commissione_pct_pratica: clamped })

    // Se era già stata scelta una compagnia, resetta la scelta perché il totale cambia.
    if (data.scelta_compagnia) {
      update({ scelta_compagnia: null, totale_finale: null })
    }
  }

  // Quixa e Sogessur NON includono il valore a nuovo nella tariffa base.
  // Qui (nel confronto) l'operatore può richiederlo esplicitamente: di norma
  // resta vuoto = nessun VAN. Se valorizzato, il totale di Quixa/Sogessur si
  // aggiorna in tempo reale (il ricalcolo è reattivo su data.dati_comuni).
  const mostraVanQS = (data.compagnie_selezionate || []).some(c => c === 'QUIXA' || c === 'SOGESSUR')
  function setVanQS(val) {
    const v = val === '' ? null : Math.max(0, parseInt(val, 10) || 0)
    update({
      dati_comuni: { ...data.dati_comuni, mesi_valore_a_nuovo_qs: v },
      // Il totale cambia → annulla l'eventuale scelta già fatta.
      ...(data.scelta_compagnia ? { scelta_compagnia: null, totale_finale: null } : {}),
    })
  }

  function scegli(codice) {
    const r = data.preventivi[codice]
    if (!r || !r.ok) return
    const premioFinale = (r.totale || 0) + standaloneTotal
    const tot = totaleConRicarico(premioFinale)
    update({
      scelta_compagnia: codice,
      totale_finale: tot,
    })
  }

  return (
    <div>
      <div className="info-box mt-1" style={{ marginBottom: '1.5rem' }}>
        Sono mostrate tutte le compagnie disponibili per <strong>{regione || 'la regione del cliente'}</strong>.
        Ogni card riporta il <strong>totale</strong> {haStandalone ? '(polizza + stand-alone già sommati)' : '(polizza)'} per quella compagnia.
        Clicca per scegliere quella con cui chiudere la pratica.
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 className="card-title">Il tuo guadagno su questa pratica</h2>
        <p className="text-small text-muted" style={{ marginBottom: '0.75rem' }}>
          Scegli la percentuale di ricarico da applicare al premio finale (range 0&ndash;{commissioneMax}%, parte da 0).
          La modifica aggiorna in tempo reale il totale di tutte le compagnie comparate.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="range" min="0" max={commissioneMax} step="0.5"
            value={surplus}
            onChange={e => setSurplus(e.target.value)}
            style={{ flex: 1 }}
          />
          <input
            type="number" min="0" max={commissioneMax} step="0.5"
            value={surplus}
            onChange={e => setSurplus(e.target.value)}
            style={{ width: 80 }}
          />
          <span className="text-small text-muted">%</span>
        </div>
      </div>

      {data.compagnie_selezionate.length === 0 ? (
        <div className="alert alert-error">
          Nessuna compagnia disponibile per la combinazione corrente di ruolo, tipo operazione e regione cliente.
          Torna indietro e verifica i dati.
        </div>
      ) : (
        <div className="comp-grid">
          {data.compagnie_selezionate.map(codice => {
            const c = compagnie.find(x => x.codice === codice)
            const r = data.preventivi[codice]
            const isSel = data.scelta_compagnia === codice
            const ok = r?.ok
            const premioFinaleBase = ok ? (r.totale || 0) + standaloneTotal : null
            const ricaricoEur = ok ? calcolaRicarico(premioFinaleBase) : null
            const totaleFinale = ok ? totaleConRicarico(premioFinaleBase) : null

            return (
              <div key={codice}
                   className={`comp-card ${isSel ? 'selected' : ''} ${!ok ? 'unavailable' : ''}`}
                   onClick={() => ok && scegli(codice)}
                   style={{ cursor: ok ? 'pointer' : 'not-allowed' }}>
                <div className="comp-name">{c?.nome || codice}</div>
                {codice === 'COVEA' && (
                  <div className="info-box" style={{ marginTop: 6, padding: '8px 10px', fontSize: 12, lineHeight: 1.4 }}>
                    Valore a nuovo: massimo <strong>24 mesi</strong> per qualsiasi preventivo.
                  </div>
                )}
                {ok ? (() => {
                  // Il ricarico si applica al premio finale visibile, quindi include
                  // La competenza fissa contrattuale è inclusa nel totale ma NON viene
                  // mostrata: non è considerata guadagno del dealer.
                  const guadagnoEur = ricaricoEur
                  // Riga "broker N%" volutamente NASCOSTA in UI (richiesta committente).
                  // Il calcolo del broker resta invariato nel motore di calcolo.
                  return (
                    <>
                      <div className="comp-total">{formatEuro(totaleFinale)}</div>
                      {haStandalone && (
                        <div className="text-small text-muted" style={{ marginTop: 2 }}>
                          polizza {formatEuro(r.totale)} + stand-alone {formatEuro(standaloneTotal)}
                        </div>
                      )}
                      {surplus > 0 && (
                        <div className="text-small text-muted" style={{ marginTop: 2 }}>
                          premio finale {formatEuro(premioFinaleBase)} + ricarico {surplus}% ({formatEuro(ricaricoEur)})
                        </div>
                      )}
                      <div className="comp-month">≈ {formatEuro((totaleFinale || 0) / (data.dati_comuni.durata_mesi || 12))}/mese</div>
                      <div className="text-small text-muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
                        tuo guadagno {surplus}% ({formatEuro(guadagnoEur)})
                      </div>
                    </>
                  )
                })() : (() => {
                  // Messaggi puliti per casi noti, fallback verbose negli altri casi
                  const reason = r?.reason_code
                  const errorsTxt = (r?.errors || []).join('; ')
                  if (reason === 'PROVINCE_NOT_ALLOWED') {
                    return (
                      <>
                        <div className="comp-tag" style={{ background: '#fef3c7', color: '#92400e' }}>Provincia non ammissibile</div>
                        <div className="text-small text-muted">Per questa compagnia la provincia del cliente non rientra nelle aree di copertura.</div>
                      </>
                    )
                  }
                  if (reason === 'VEHICLE_NOT_ALLOWED') {
                    return (
                      <>
                        <div className="comp-tag" style={{ background: '#fef3c7', color: '#92400e' }}>🚐 Veicolo non ammesso</div>
                        <div className="text-small text-muted">{errorsTxt}</div>
                      </>
                    )
                  }
                  return (
                    <>
                      <div className="comp-tag">⚠ tariffa non disponibile</div>
                      <div className="text-small text-muted">{errorsTxt}</div>
                    </>
                  )
                })()}
              </div>
            )
          })}
        </div>
      )}

      {mostraVanQS && (
        <>
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 className="card-title">Valore a nuovo — Quixa / Sogessur</h2>
            <p className="text-small text-muted" style={{ marginBottom: '0.75rem' }}>
              Le tariffe <strong>Quixa</strong> e <strong>Sogessur</strong> non includono il valore a nuovo.
              Di norma questo campo resta <strong>vuoto</strong> (nessun valore a nuovo aggiunto):
              inseriscilo solo se il cliente lo richiede. I totali di Quixa/Sogessur si aggiornano in tempo reale.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="number" min="0" max={data.dati_comuni.durata_mesi || 60} step="1"
                value={data.dati_comuni.mesi_valore_a_nuovo_qs ?? ''}
                onFocus={e => e.target.select()}
                onChange={e => setVanQS(e.target.value)}
                placeholder={`vuoto = nessun valore a nuovo · max ${data.dati_comuni.durata_mesi || 60}`}
                style={{ width: 240 }}
              />
              <span className="text-small text-muted">mesi di valore a nuovo</span>
            </div>
          </div>
          <div className="info-box" style={{ marginTop: '0.75rem' }}>
            <strong>Per mensilizzare con Compass:</strong> premio polizza x 0.107
          </div>
        </>
      )}

      {standaloneTotal > 0 && (
        <div className="card mt-3">
          <strong>Stand alone:</strong>
          <ul style={{ margin: '8px 0 0 1.5rem' }}>
            {(data.standalone_selezionati || []).map(sel => {
              const prod = standalone.find(p => p.codice === sel.codice)
              if (!prod) return null
              const tot = parseFloat(prod.prezzo_annuo) * sel.anni
              return (
                <li key={sel.codice}>
                  {prod.nome} · {sel.anni} {sel.anni === 1 ? 'anno' : 'anni'} = <strong>{formatEuro(tot)}</strong>
                </li>
              )
            })}
          </ul>
          <div className="mt-1"><strong>Totale stand alone:</strong> {formatEuro(standaloneTotal)}</div>
        </div>
      )}

      {data.scelta_compagnia && (
        <div className="total-box purple mt-3">
          <div className="tl">Totale pratica con compagnia scelta</div>
          <div className="tv">{formatEuro(data.totale_finale)}</div>
        </div>
      )}
    </div>
  )
}

function StepComparazioneAutocarri({ data, update, commissioneMax = 50, commissioneAccountPct = 0, autocarriParametri = {} }) {
  const dc = data.dati_comuni || {}
  const pesoLabel = dc.peso_autocarro === 'fino_35' ? 'FINO A 35 QUINTALI' : 'OLTRE I 35 QUINTALI'
  const surplus = data.commissione_pct_pratica ?? 0

  function round2(n) { return Math.round(n * 100) / 100 }
  function totaleConRicarico(base) {
    return round2((parseFloat(base) || 0) * (1 + (parseFloat(surplus) || 0) / 100))
  }

  function setSurplus(val) {
    const clamped = Math.min(commissioneMax, Math.max(0, parseFloat(val) || 0))
    const r = data.preventivi?.[AUTOCARRI_INTERMEDIARI_CODICE]
    update({
      commissione_pct_pratica: clamped,
      ...(data.scelta_compagnia === AUTOCARRI_INTERMEDIARI_CODICE
        ? { totale_finale: r?.ok ? round2((parseFloat(r.totale) || 0) * (1 + clamped / 100)) : null }
        : {}),
    })
  }

  function preventivoPer(pacchetto, massimale = null) {
    return calcolaAutocarriIntermediari({
      dati: {
        ...dc,
        categoria_veicolo: 'autocarro',
        durata_mesi: 12,
        pacchetto_autocarro: pacchetto,
        cristalli_massimale: pacchetto === 'COMPLETO' ? massimale : null,
      },
      paramComp: autocarriParametri,
      commissioneDealerPct: commissioneAccountPct,
    })
  }

  function scegli(pacchetto, massimale = null) {
    const r = preventivoPer(pacchetto, massimale)
    if (!r.ok) return
    update({
      dati_comuni: {
        ...dc,
        categoria_veicolo: 'autocarro',
        durata_mesi: 12,
        satellitare: false,
        sat_marca: '',
        sat_seriale: '',
        acquista_lojack: false,
        mesi_lojack: null,
        pacchetto_autocarro: pacchetto,
        cristalli_massimale: pacchetto === 'COMPLETO' ? massimale : null,
      },
      compagnie_selezionate: [AUTOCARRI_INTERMEDIARI_CODICE],
      preventivi: { [AUTOCARRI_INTERMEDIARI_CODICE]: r },
      scelta_compagnia: AUTOCARRI_INTERMEDIARI_CODICE,
      totale_finale: totaleConRicarico(r.totale),
    })
  }

  const valoreNonAssicurabile = parseFloat(dc.valore_auto) > 300000

  return (
    <div>
      <div className="info-box mt-1" style={{ marginBottom: '1.5rem' }}>
        Seleziona il pacchetto per la polizza autocarri. I costi sono calcolati su <strong>{pesoLabel}</strong>
        {' '}con durata fissa <strong>12 mesi</strong>.
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 className="card-title">Il tuo guadagno su questa pratica</h2>
        <p className="text-small text-muted" style={{ marginBottom: '0.75rem' }}>
          Scegli la percentuale di ricarico da applicare al premio finale (range 0&ndash;{commissioneMax}%, parte da 0).
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="range" min="0" max={commissioneMax} step="0.5"
            value={surplus}
            onChange={e => setSurplus(e.target.value)}
            style={{ flex: 1 }}
          />
          <input
            type="number" min="0" max={commissioneMax} step="0.5"
            value={surplus}
            onChange={e => setSurplus(e.target.value)}
            style={{ width: 80 }}
          />
          <span className="text-small text-muted">%</span>
        </div>
      </div>

      {valoreNonAssicurabile ? (
        <div className="alert alert-error">
          Valore veicolo superiore a 300.000 EUR: il rischio non è assicurabile.
        </div>
      ) : (
        <div className="comp-grid">
          {PACCHETTI_AUTOCARRO.map(p => {
            const isCompleto = p.value === 'COMPLETO'
            const selected = dc.pacchetto_autocarro === p.value
            const massimaleCorrente = dc.cristalli_massimale || 650
            const r = preventivoPer(p.value, isCompleto ? massimaleCorrente : null)
            const ok = r.ok
            const totaleFinale = ok ? totaleConRicarico(r.totale) : null

            return (
              <div key={p.value}
                   className={`comp-card ${selected ? 'selected' : ''} ${!ok ? 'unavailable' : ''}`}
                   onClick={() => !isCompleto && ok && scegli(p.value)}
                   style={{ cursor: ok && !isCompleto ? 'pointer' : 'default' }}>
                <div className="comp-name">{p.label}</div>
                <div className="text-small text-muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
                  {p.garanzie.join(', ')}
                </div>

                {isCompleto && (
                  <div style={{ marginTop: 12 }}>
                    <div className="text-small text-muted" style={{ marginBottom: 6 }}>
                      Massimale cristalli
                    </div>
                    <div className="radio-group">
                      {MASSIMALI_CRISTALLI_AUTOCARRO.map(m => {
                        const rr = preventivoPer(p.value, m.value)
                        const rrTotale = rr.ok ? totaleConRicarico(rr.totale) : rr.totale
                        const isMassimale = selected && parseInt(dc.cristalli_massimale, 10) === m.value
                        return (
                          <div key={m.value}
                               className={`radio-btn ${isMassimale ? 'selected' : ''}`}
                               onClick={(e) => {
                                 e.stopPropagation()
                                 scegli(p.value, m.value)
                            }}>
                            {m.label}
                            <div className="text-small text-muted">{formatEuro(rrTotale)}</div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {ok ? (
                  <>
                    <div className="comp-total" style={{ marginTop: 12 }}>
                      {formatEuro(totaleFinale)}
                    </div>
                    {surplus > 0 && (
                      <div className="text-small text-muted" style={{ marginTop: 2 }}>
                        premio {formatEuro(r.totale)} + ricarico {surplus}% ({formatEuro(totaleFinale - r.totale)})
                      </div>
                    )}
                    <div className="comp-month">≈ {formatEuro((totaleFinale || 0) / 12)}/mese</div>
                  </>
                ) : (
                  <>
                    <div className="comp-tag">⚠ tariffa non disponibile</div>
                    <div className="text-small text-muted">{(r.errors || []).join('; ')}</div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {data.scelta_compagnia === AUTOCARRI_INTERMEDIARI_CODICE && (
        <div className="total-box purple mt-3">
          <div className="tl">Totale pratica autocarri</div>
          <div className="tv">{formatEuro(data.totale_finale)}</div>
        </div>
      )}
    </div>
  )
}
